import { useCallback, useEffect, useMemo, useState } from "react"

import type { AgentType, TemplatedJson } from "@productlane/lib"

import type { RefsState } from "../model/refs-state"
import type { SecureRefOption } from "../model/secure-ref-picker"
import type { JsonKind } from "../model/tool-schema-fields"
import { SecureRefAutocomplete } from "../inputs/secure-ref-autocomplete"
import { useDebouncedValue } from "../inputs/use-debounced-value"
import {
	formatJsonDraft,
	parseJsonDraft,
	toJsonDraftText,
} from "../model/json-value-draft"

const SAVE_DELAY_MS = 600
const ERROR_DELAY_MS = 300

interface Props {
	name: string
	value: TemplatedJson
	kinds: JsonKind[]
	knownKeys?: readonly string[]
	refOptions: readonly SecureRefOption[]
	refsState: RefsState
	onRetryRefs: () => void
	/** Types a leaf may carry, offered as `agent.<type>` rows inside a token. */
	agentTypes?: readonly AgentType[]
	onCommit: (value: TemplatedJson) => void
}

export function JsonValueEditor({
	name,
	value,
	kinds,
	knownKeys,
	refOptions,
	refsState,
	onRetryRefs,
	agentTypes,
	onCommit,
}: Props) {
	// Every field here is storage form; the autocomplete owns the display form, so
	// a connector name never reaches the parser.
	const [editor, setEditor] = useState(() => {
		const stored = toJsonDraftText(value)
		return { text: stored, seen: stored, committed: stored }
	})
	const [revealError, setRevealError] = useState(false)

	const incoming = toJsonDraftText(value)
	if (incoming !== editor.seen) {
		if (incoming === editor.committed) {
			setEditor((prev) => ({ ...prev, seen: incoming }))
		} else {
			setEditor({ text: incoming, seen: incoming, committed: incoming })
		}
	}

	// String keys, so a parent re-render with fresh array literals cannot retrigger.
	const kindsKey = kinds.join(",")
	const keysKey = knownKeys?.join("\n") ?? ""
	const draft = useMemo(
		() => parseJsonDraft(editor.text, { kinds, knownKeys }),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[editor.text, kindsKey, keysKey],
	)
	const error = draft.ok ? null : draft.error

	const commit = useCallback(
		(next: TemplatedJson, nextText: string) => {
			setEditor((prev) => ({
				...prev,
				text: nextText,
				committed: toJsonDraftText(next),
			}))
			onCommit(next)
		},
		[onCommit],
	)

	const settledText = useDebouncedValue(editor.text, SAVE_DELAY_MS)
	useEffect(() => {
		if (settledText !== editor.text || !draft.ok) return
		if (toJsonDraftText(draft.value) === editor.committed) return
		commit(draft.value, editor.text)
	}, [settledText, editor, draft, commit])

	// Held back a moment so an error does not flash on every keystroke; blurring
	// with a broken draft reveals it immediately.
	const settledError = useDebouncedValue(error, ERROR_DELAY_MS)
	const shownError =
		error && (error === settledError || revealError) ? error : null

	// Reads the text it is handed rather than state, which a pick has not flushed
	// yet. Formatting is blur-only: reformatting under a pick moves the caret.
	const save = (text: string, format: boolean) => {
		const parsed = parseJsonDraft(text, { kinds, knownKeys })
		if (!parsed.ok) {
			if (format) setRevealError(true)
			return
		}
		const next = format ? formatJsonDraft(text) : text
		if (next !== text || toJsonDraftText(parsed.value) !== editor.committed) {
			commit(parsed.value, next)
		}
	}

	return (
		<div className="mt-1.5">
			<SecureRefAutocomplete
				name={name}
				mode="template"
				multiline
				value={editor.text}
				options={refOptions}
				agentTypes={agentTypes}
				errorMessage={shownError}
				extraHint={
					draft.ok && draft.warnings.length > 0 ? draft.warnings[0] : undefined
				}
				refsState={refsState}
				onRetryRefs={onRetryRefs}
				onChange={(text) => {
					setRevealError(false)
					setEditor((prev) => ({ ...prev, text }))
				}}
				onCommit={(text) => save(text, false)}
				onBlurText={(text) => save(text, true)}
			/>
		</div>
	)
}
